/**
 * Query key factories. Keys are rooted per resource so invalidation by
 * prefix works: ['feed'], ['agents'], ['me'], ['tokens'] ...
 * Session-scoped roots are listed in SESSION_ROOTS.
 */
import type { PerfRange } from '@algoboo/schema'

/** Stable key for a kind filter: sorted, comma-joined, or 'all'. */
export function kindKey(kinds?: readonly string[] | null): string {
  if (!kinds || kinds.length === 0) return 'all'
  const k = [...new Set(kinds)].filter((x) => x !== 'all').sort()
  return k.length ? k.join(',') : 'all'
}

export const qk = {
  stats: () => ['stats'] as const,
  feedRoot: () => ['feed'] as const,
  feed: (kinds: string, agent: string | null = null) => ['feed', 'global', kinds, agent] as const,
  agentFeed: (handle: string, kinds: string) => ['feed', 'agent', handle, kinds] as const,
  agents: () => ['agents'] as const,
  agentList: (range: PerfRange, sort: string, q: string, filters = '') => ['agents', 'list', range, sort, q, filters] as const,
  agent: (handle: string) => ['agents', 'profile', handle] as const,
  agentDecisions: (handle: string) => ['agents', 'decisions', handle] as const,
  agentTrades: (handle: string) => ['agents', 'trades', handle] as const,
  agentPositions: (handle: string) => ['agents', 'positions', handle] as const,
  agentPerformance: (handle: string, range: PerfRange, scope: 'all' | 'paper' | 'live' = 'all') => ['agents', 'performance', handle, range, scope] as const,
  agentVersions: (handle: string) => ['agents', 'versions', handle] as const,
  agentToken: (handle: string) => ['tokens', 'agent', handle] as const,
  agentTokenEpochs: (handle: string) => ['tokens', 'epochs', handle] as const,
  weekStandings: () => ['standings', 'week'] as const,
  tokens: () => ['tokens'] as const,
  tokenList: (sort: string) => ['tokens', 'list', sort] as const,
  token: (address: string) => ['tokens', 'detail', address.toLowerCase()] as const,
  claimable: (handle: string, holder: string) => ['claimable', handle, holder.toLowerCase()] as const,
  brains: () => ['catalog', 'brains'] as const,
  plans: () => ['catalog', 'plans'] as const,
  quoteAssets: () => ['catalog', 'quote-assets'] as const,
  /** Under the session root `me`: the listing carries the caller's waitlist state. */
  venues: () => ['me', 'venues'] as const,
  handle: (handle: string) => ['handles', handle] as const,
  evidence: (id: string) => ['evidence', id] as const,
  trending: (range: PerfRange) => ['trending', range] as const,
  creator: (handle: string) => ['creators', handle] as const,
  brainEstimate: (brainId: string, ticksPerDay: number, mode: string) => ['catalog', 'estimate', brainId, ticksPerDay, mode] as const,
  // session-scoped
  me: () => ['me'] as const,
  myWallets: () => ['me', 'wallets'] as const,
  myApiKeys: () => ['me', 'api-keys'] as const,
  following: () => ['me', 'following'] as const,
  /** Posts of followed agents (`GET /feed?scope=following`); under `me` so sign-out and follow changes drop it. */
  followingFeed: (kinds: string) => ['me', 'feed', 'following', kinds] as const,
  credits: () => ['me', 'credits'] as const,
  subscriptions: () => ['me', 'subscriptions'] as const,
  payments: () => ['me', 'payments'] as const,
  payment: (id: string) => ['me', 'payments', 'detail', id] as const,
  /** A pending intent with its wallet txs, parked by the agent page for the billing page. */
  pendingPayment: (id: string) => ['me', 'payments', 'pending-result', id] as const,
  myProposals: (status: string | null) => ['me', 'proposals', status] as const,
  manage: (handle: string) => ['manage', handle] as const,
  manageUsage: (handle: string, range: PerfRange) => ['manage', handle, 'usage', range] as const,
  myAgents: (status: string | null = null) => ['me', 'agents', status] as const,
  notifications: (unread: boolean) => ['me', 'notifications', 'list', unread] as const,
  unreadCount: () => ['me', 'notifications', 'unread'] as const,
  /** Paid agent creation: credits and the interview (session-scoped, under `me`). */
  creationCredits: () => ['me', 'creation', 'credits'] as const,
  // trading wallets and permissions (arbitrage custody), all under `me`
  accountWallet: () => ['me', 'trading-wallet'] as const,
  portfolio: () => ['me', 'portfolio'] as const,
  permissions: (wallet: string | null = null) => ['me', 'permissions', wallet] as const,
  permissionOptions: () => ['me', 'permission-options'] as const,
  walletStatement: () => ['me', 'wallet-statement'] as const,
  orders: (wallet: string | null = null) => ['me', 'orders', wallet] as const,
  // customer and holder (milestone 3, L4)
  myHoldings: () => ['me', 'holdings'] as const,
  myInvoices: (status: string | null = null) => ['me', 'invoices', status] as const,
  agentPricing: (handle: string) => ['agents', 'pricing', handle] as const,
}

/** Roots whose data depends on who is signed in. */
export const SESSION_ROOTS = [['me'], ['manage'], ['claimable'], ['admin']] as const
